import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import ExpenseUserWebcamDialog from "../ExpensesUser/ExpenseUserWebcamDialog";
import { getResponsablesTodos } from "../../api/Proyectos/ProyectosTareasApi";
import { getProveedores } from "../../api/ProveedoresyFabricas/proveedoresApi";
import { getFabricas } from "../../api/ProveedoresyFabricas/fabricasApi";
import {
  getAllMateriales,
  getGrupos,
  getSubgrupos,
  uploadMaterialFotos,
  deleteMaterialFotoById,
  deleteMaterialFoto,
  materialFotoPublicUrl,
} from "../../api/Materialoteca/MaterialotecaApi";
import {
  addFeriaMaterial,
  createFeriaProveedorNuevo,
  deleteFeriaMaterial,
  deleteFeriaProveedor,
  extractFeriaFoto,
  getFeriaEdicion,
  linkFeriaProveedor,
  updateFeriaAsistentes,
  updateFeriaEdicion,
  updateFeriaMaterial,
  updateFeriaProveedor,
  uploadFeriaProveedorFoto,
} from "../../api/Ferias/FeriasApi";
import FeriaEstadoChip from "./FeriaEstadoChip";
import { MUESTRA_ESTADOS, materialPrimaryFotoArchivo } from "./feriaStatus";
import MaterialotecaPhotoLightbox from "../Materialoteca/MaterialotecaPhotoLightbox";

/** En móvil: input capture. En PC: webcam dialog (capture en desktop puede colgarse). */
const shouldUseCameraCapture = () =>
  typeof navigator !== "undefined" &&
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent || "",
  );

const emptySupplierForm = () => ({
  CODPROV: "",
  id_Fabrica: "",
  kind: "trader",
  Nombre: "",
  Pais: "",
  Email: "",
  Telefono: "",
  Direccion: "",
  NVIA: "",
  CP: "",
  Ciudad: "",
  Provincia: "",
  Stand: "",
  photoFile: null,
});

const emptyMaterialForm = () => ({
  id_Materialoteca: "",
  NombreMaterial: "",
  ID_GRUPO: "",
  TipoMaterial: "",
  Composicion: "",
  Peso: "",
  AnchoTirada: "",
  MinimumOrderQty: "",
  Precio: "",
  Proveedor: "",
  id_Fabrica: "",
  MillName: "",
  pedir: false,
  Estado: "catalogado",
  extractName: "",
  photoFile: null,
});

/** Opciones de trader/mill ya vinculados a la edición. */
const editionSupplierOptions = (proveedores = []) =>
  (proveedores || [])
    .map((row) => {
      const CODPROV = row.CODPROV || row.proveedor?.CODPROV || null;
      const id_Fabrica = row.id_Fabrica || row.fabrica?.id_Fabrica || null;
      const label =
        row.proveedor?.N_PROVEEDOR || row.fabrica?.NombreFabrica || null;
      if (!label || (!CODPROV && !id_Fabrica)) return null;
      return {
        key: CODPROV ? `t-${CODPROV}` : `f-${id_Fabrica}`,
        CODPROV: CODPROV ? Number(CODPROV) : null,
        id_Fabrica: id_Fabrica ? Number(id_Fabrica) : null,
        label,
        kind: CODPROV ? "trader" : "mill",
      };
    })
    .filter(Boolean);

const ACCENT = "#ffac7e";

const fieldSx = (theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.black, 0.22)
        : alpha(theme.palette.background.paper, 0.92),
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(ACCENT, 0.7),
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.18)}`,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ACCENT,
  },
});

const FormSection = ({ icon: Icon, title, children }) => {
  const theme = useTheme();
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
        {Icon ? <Icon sx={{ fontSize: 18, color: ACCENT }} /> : null}
        <Typography
          variant="overline"
          sx={{
            fontWeight: 800,
            letterSpacing: 0.6,
            color: theme.palette.text.secondary,
            lineHeight: 1,
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Stack spacing={1.5}>{children}</Stack>
    </Box>
  );
};

/**
 * Edition detail (right pane): Stage people, suppliers, materials + add dialogs.
 * @param {{ edicionId: number|string, onChanged?: () => void, showHeader?: boolean, focusSection?: string, focusItemId?: number|null, onFocusItem?: (focus: string, focusId: number|null) => void, headerHeight?: number }} props
 */
const FeriaEdicionPanel = ({
  edicionId,
  onChanged,
  showHeader = false,
  focusSection = "overview",
  focusItemId = null,
  onFocusItem,
  headerHeight = 104,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const inputSx = fieldSx(theme);
  const [edicion, setEdicion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [traders, setTraders] = useState([]);
  const [mills, setMills] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [subgrupos, setSubgrupos] = useState([]);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [materialOpen, setMaterialOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [editMaterial, setEditMaterial] = useState(null);
  const [editSupplierForm, setEditSupplierForm] = useState({
    Nombre: "",
    Pais: "",
    Email: "",
    Telefono: "",
    Direccion: "",
    NVIA: "",
    CP: "",
    Ciudad: "",
    Provincia: "",
    Stand: "",
    Notas: "",
    isTrader: true,
  });
  const [editMaterialForm, setEditMaterialForm] = useState({
    NombreMaterial: "",
    Composicion: "",
    MillName: "",
    Proveedor: "",
    id_Fabrica: "",
    Peso: "",
    AnchoTirada: "",
    MinimumOrderQty: "",
    Precio: "",
    Estado: "catalogado",
    Notas: "",
    Fotos: [],
  });
  const [editPhotoBusy, setEditPhotoBusy] = useState(false);
  const [photoLightboxSrc, setPhotoLightboxSrc] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [webcamKind, setWebcamKind] = useState("card");
  const [supplierMode, setSupplierMode] = useState("existing-trader");
  const [supplierForm, setSupplierForm] = useState(emptySupplierForm);
  const [materialForm, setMaterialForm] = useState(emptyMaterialForm);
  /** Proveedor de la edición seleccionado para filtrar / añadir materiales. */
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [highlightMaterialId, setHighlightMaterialId] = useState(null);
  const useNativeCamera = shouldUseCameraCapture();

  const patchEditSupplier = (patch) =>
    setEditSupplierForm((prev) => ({ ...prev, ...patch }));
  const patchEditMaterial = (patch) =>
    setEditMaterialForm((prev) => ({ ...prev, ...patch }));
  const patchSupplierForm = (patch) =>
    setSupplierForm((prev) => ({ ...prev, ...patch }));
  const patchMaterialForm = (patch) =>
    setMaterialForm((prev) => ({ ...prev, ...patch }));

  const pickFileInput = (kind) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (kind === "material-photo") {
        patchMaterialForm({ photoFile: file });
        return;
      }
      handlePhotoExtract(file, kind);
    };
    input.click();
  };

  const openCamera = (kind) => {
    if (useNativeCamera) {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.setAttribute("capture", "environment");
      input.onchange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (kind === "material-photo") {
          patchMaterialForm({ photoFile: file });
          return;
        }
        handlePhotoExtract(file, kind);
      };
      input.click();
      return;
    }
    setWebcamKind(kind);
    setWebcamOpen(true);
  };

  const photoButtons = (kind, { takeLabel, galleryLabel } = {}) => (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
      <Button
        startIcon={<PhotoCameraOutlinedIcon />}
        variant="outlined"
        disabled={extracting && kind !== "material-photo"}
        onClick={() => openCamera(kind)}
        sx={{ textTransform: "none", fontWeight: 700 }}
      >
        {extracting && kind !== "material-photo"
          ? t("FeriasPage.readingPhoto")
          : takeLabel || t("FeriasPage.takePhoto")}
      </Button>
      <Button
        startIcon={<ImageOutlinedIcon />}
        variant="outlined"
        disabled={extracting && kind !== "material-photo"}
        onClick={() => pickFileInput(kind)}
        sx={{ textTransform: "none", fontWeight: 700 }}
      >
        {galleryLabel || t("FeriasPage.chooseGallery")}
      </Button>
    </Stack>
  );

  const load = async () => {
    const data = await getFeriaEdicion(edicionId);
    setEdicion(data);
    return data;
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getFeriaEdicion(edicionId),
      getResponsablesTodos().catch(() => []),
      getProveedores().catch(() => []),
      getFabricas().catch(() => []),
      getAllMateriales().catch(() => []),
      getGrupos().catch(() => []),
    ])
      .then(([data, u, tr, mi, mats, gr]) => {
        if (cancelled) return;
        setEdicion(data);
        setUsers(Array.isArray(u) ? u : []);
        setTraders(Array.isArray(tr) ? tr : []);
        setMills(Array.isArray(mi) ? mi : []);
        setMateriales(Array.isArray(mats) ? mats : []);
        setGrupos(Array.isArray(gr) ? gr : []);
      })
      .catch((err) => {
        console.error("❌ FeriaEdicionPanel:", err);
        if (!cancelled) setEdicion(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [edicionId]);

  useEffect(() => {
    if (!edicion || focusSection === "overview") return;
    const id =
      focusSection === "suppliers"
        ? "feria-section-suppliers"
        : focusSection === "materials"
          ? "feria-section-materials"
          : null;
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [edicion, focusSection]);

  /** Sincroniza clic del árbol → selección en el panel derecho. */
  useEffect(() => {
    if (!edicion) return;

    if (focusSection === "suppliers") {
      if (focusItemId != null) {
        setSelectedSupplierId(Number(focusItemId));
        setHighlightMaterialId(null);
      }
      return;
    }

    if (focusSection === "materials" && focusItemId != null) {
      const mat = (edicion.materiales || []).find(
        (m) => Number(m.id_FeriaMaterial) === Number(focusItemId),
      );
      if (!mat) return;
      const cod = mat.CODPROV || mat.material?.Proveedor;
      const fab = mat.id_Fabrica;
      const owner = (edicion.proveedores || []).find((p) => {
        const pCod = p.CODPROV || p.proveedor?.CODPROV;
        const pFab = p.id_Fabrica || p.fabrica?.id_Fabrica;
        if (cod && pCod && Number(pCod) === Number(cod)) return true;
        if (fab && pFab && Number(pFab) === Number(fab)) return true;
        return false;
      });
      if (owner) setSelectedSupplierId(owner.id_FeriaProveedor);
      setHighlightMaterialId(Number(focusItemId));
      return;
    }

    if (focusSection === "overview") {
      setHighlightMaterialId(null);
    }
  }, [edicion, focusSection, focusItemId]);

  useEffect(() => {
    if (materialForm.ID_GRUPO) {
      getSubgrupos(materialForm.ID_GRUPO).then(setSubgrupos);
    } else {
      setSubgrupos([]);
    }
  }, [materialForm.ID_GRUPO]);

  const selectedUsers = useMemo(() => {
    const ids = new Set((edicion?.asistentes || []).map((a) => a.id_User));
    return users.filter((u) => ids.has(u.id));
  }, [edicion, users]);

  const supplierOptions = useMemo(
    () => editionSupplierOptions(edicion?.proveedores),
    [edicion?.proveedores],
  );

  const selectedMaterialSupplier = useMemo(() => {
    if (materialForm.Proveedor) {
      return (
        supplierOptions.find(
          (o) => Number(o.CODPROV) === Number(materialForm.Proveedor),
        ) || null
      );
    }
    if (materialForm.id_Fabrica) {
      return (
        supplierOptions.find(
          (o) => Number(o.id_Fabrica) === Number(materialForm.id_Fabrica),
        ) || null
      );
    }
    return null;
  }, [supplierOptions, materialForm.Proveedor, materialForm.id_Fabrica]);

  const selectedEditMaterialSupplier = useMemo(() => {
    if (editMaterialForm.Proveedor) {
      return (
        supplierOptions.find(
          (o) => Number(o.CODPROV) === Number(editMaterialForm.Proveedor),
        ) || null
      );
    }
    if (editMaterialForm.id_Fabrica) {
      return (
        supplierOptions.find(
          (o) => Number(o.id_Fabrica) === Number(editMaterialForm.id_Fabrica),
        ) || null
      );
    }
    return null;
  }, [
    supplierOptions,
    editMaterialForm.Proveedor,
    editMaterialForm.id_Fabrica,
  ]);

  const applySupplierToMaterialForm = (opt, patcher) => {
    if (!opt) {
      patcher({ Proveedor: "", id_Fabrica: "", MillName: "" });
      return;
    }
    if (opt.CODPROV) {
      patcher({
        Proveedor: opt.CODPROV,
        id_Fabrica: "",
        MillName: opt.kind === "mill" ? opt.label : "",
      });
      return;
    }
    patcher({
      Proveedor: "",
      id_Fabrica: opt.id_Fabrica || "",
      MillName: opt.label || "",
    });
  };

  const selectedSupplierRow = useMemo(
    () =>
      (edicion?.proveedores || []).find(
        (p) => Number(p.id_FeriaProveedor) === Number(selectedSupplierId),
      ) || null,
    [edicion?.proveedores, selectedSupplierId],
  );

  const materialBelongsToSupplier = (matRow, supplierRow) => {
    if (!supplierRow) return true;
    const cod = supplierRow.CODPROV || supplierRow.proveedor?.CODPROV;
    const fab = supplierRow.id_Fabrica || supplierRow.fabrica?.id_Fabrica;
    if (cod) {
      return (
        Number(matRow.CODPROV) === Number(cod) ||
        Number(matRow.material?.Proveedor) === Number(cod)
      );
    }
    if (fab) {
      return Number(matRow.id_Fabrica) === Number(fab);
    }
    return false;
  };

  const filteredMaterials = useMemo(() => {
    const list = edicion?.materiales || [];
    if (!selectedSupplierRow) return list;
    return list.filter((m) =>
      materialBelongsToSupplier(m, selectedSupplierRow),
    );
  }, [edicion?.materiales, selectedSupplierRow]);

  useEffect(() => {
    if (!highlightMaterialId) return;
    const t = setTimeout(() => {
      const el = document.getElementById(
        `feria-material-${highlightMaterialId}`,
      );
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 80);
    return () => clearTimeout(t);
  }, [highlightMaterialId, selectedSupplierId, filteredMaterials.length]);

  const openAddMaterialForSupplier = (supplierRow) => {
    if (!supplierRow) {
      // eslint-disable-next-line no-alert
      window.alert(t("FeriasPage.selectSupplierFirst"));
      return;
    }
    const base = emptyMaterialForm();
    const cod = supplierRow.CODPROV || supplierRow.proveedor?.CODPROV;
    const fab = supplierRow.id_Fabrica || supplierRow.fabrica?.id_Fabrica;
    const label =
      supplierRow.proveedor?.N_PROVEEDOR ||
      supplierRow.fabrica?.NombreFabrica ||
      "";
    if (cod) {
      base.Proveedor = Number(cod);
      base.id_Fabrica = "";
      base.MillName = "";
    } else if (fab) {
      base.Proveedor = "";
      base.id_Fabrica = Number(fab);
      base.MillName = label;
    }
    setSelectedSupplierId(supplierRow.id_FeriaProveedor);
    setMaterialForm(base);
    setMaterialOpen(true);
  };

  // Si desaparece el proveedor seleccionado tras un reload, limpia selección
  useEffect(() => {
    if (!selectedSupplierId || !edicion?.proveedores) return;
    const stillThere = (edicion.proveedores || []).some(
      (p) => Number(p.id_FeriaProveedor) === Number(selectedSupplierId),
    );
    if (!stillThere) setSelectedSupplierId(null);
  }, [edicion?.proveedores, selectedSupplierId]);

  const notifyChanged = async () => {
    const data = await load();
    if (onChanged) onChanged();
    return data;
  };

  const syncEditMaterialFromEdicion = (data, feriaMaterialId) => {
    const refreshed = (data?.materiales || []).find(
      (m) => Number(m.id_FeriaMaterial) === Number(feriaMaterialId),
    );
    if (!refreshed) return;
    setEditMaterial(refreshed);
    setEditMaterialForm((prev) => ({
      ...prev,
      Fotos: refreshed.material?.Fotos || [],
    }));
  };

  const handleEditMaterialPhotos = async (files) => {
    const materialId =
      editMaterial?.id_Materialoteca || editMaterial?.material?.Id;
    if (!materialId || !files?.length) return;
    setEditPhotoBusy(true);
    try {
      await uploadMaterialFotos(materialId, files);
      const data = await notifyChanged();
      syncEditMaterialFromEdicion(data, editMaterial.id_FeriaMaterial);
    } catch (err) {
      console.error("❌ upload fotos material feria:", err);
    } finally {
      setEditPhotoBusy(false);
    }
  };

  const handleRemoveEditMaterialPhoto = async (foto) => {
    const materialId =
      editMaterial?.id_Materialoteca || editMaterial?.material?.Id;
    if (!materialId) return;
    setEditPhotoBusy(true);
    try {
      if (Number.isFinite(Number(foto.id_MaterialotecaFoto))) {
        await deleteMaterialFotoById(materialId, foto.id_MaterialotecaFoto);
      } else {
        await deleteMaterialFoto(materialId);
      }
      const data = await notifyChanged();
      syncEditMaterialFromEdicion(data, editMaterial.id_FeriaMaterial);
    } catch (err) {
      console.error("❌ delete foto material feria:", err);
    } finally {
      setEditPhotoBusy(false);
    }
  };

  const pickEditMaterialPhotos = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length) handleEditMaterialPhotos(files);
    };
    input.click();
  };

  const handlePhotoExtract = async (file, kind) => {
    if (!file) return;
    setExtracting(true);
    try {
      const { extracted } = await extractFeriaFoto(file, kind);
      if (kind === "card") {
        setSupplierForm((prev) => ({
          ...prev,
          Nombre: extracted.Nombre || prev.Nombre,
          Pais: extracted.Pais || prev.Pais,
          Email: extracted.Email || prev.Email,
          Telefono: extracted.Telefono || prev.Telefono,
          Direccion: extracted.Direccion || prev.Direccion,
          kind: extracted.kindHint === "mill" ? "mill" : prev.kind,
          photoFile: file,
        }));
        if (extracted.kindHint === "mill") setSupplierMode("new");
      } else {
        setMaterialForm((prev) => ({
          ...prev,
          NombreMaterial: extracted.NombreMaterial || prev.NombreMaterial,
          Composicion: extracted.Composicion || prev.Composicion,
          Peso: extracted.Peso ?? prev.Peso,
          AnchoTirada: extracted.AnchoTirada ?? prev.AnchoTirada,
          MinimumOrderQty: extracted.MinimumOrderQty ?? prev.MinimumOrderQty,
          Precio: extracted.Precio ?? prev.Precio,
          MillName: extracted.MillName || prev.MillName,
          extractName: file.name,
          // La foto de ficha/etiqueta no es la foto del tejido
        }));
      }
    } catch (err) {
      console.error("❌ extract feria:", err);
      if (kind === "card")
        setSupplierForm((prev) => ({ ...prev, photoFile: file }));
      else
        setMaterialForm((prev) => ({
          ...prev,
          extractName: file.name,
        }));
    } finally {
      setExtracting(false);
    }
  };

  const handleSaveSupplier = async () => {
    let created;
    if (supplierMode === "existing-trader") {
      created = await linkFeriaProveedor(edicionId, {
        CODPROV: supplierForm.CODPROV,
        Stand: supplierForm.Stand,
      });
    } else if (supplierMode === "existing-mill") {
      created = await linkFeriaProveedor(edicionId, {
        id_Fabrica: supplierForm.id_Fabrica,
        Stand: supplierForm.Stand,
      });
    } else {
      created = await createFeriaProveedorNuevo(edicionId, {
        kind: supplierForm.kind,
        Nombre: supplierForm.Nombre,
        Pais: supplierForm.Pais,
        Email: supplierForm.Email,
        Telefono: supplierForm.Telefono,
        Direccion: supplierForm.Direccion,
        NVIA: supplierForm.NVIA,
        CP: supplierForm.CP,
        Ciudad: supplierForm.Ciudad,
        Provincia: supplierForm.Provincia,
        Stand: supplierForm.Stand,
      });
    }
    if (supplierForm.photoFile && created?.id_FeriaProveedor) {
      await uploadFeriaProveedorFoto(
        created.id_FeriaProveedor,
        supplierForm.photoFile,
      );
    }
    setSupplierOpen(false);
    await notifyChanged();
  };

  const handleSaveMaterial = async () => {
    if (!materialForm.Proveedor && !materialForm.id_Fabrica) {
      // eslint-disable-next-line no-alert
      window.alert(t("FeriasPage.supplierRequiredForMaterial"));
      return;
    }
    const payload = materialForm.id_Materialoteca
      ? {
          id_Materialoteca: materialForm.id_Materialoteca,
          pedir: materialForm.pedir,
          Estado: materialForm.pedir ? "pendiente_llegar" : materialForm.Estado,
          Proveedor: materialForm.Proveedor || null,
          CODPROV: materialForm.Proveedor || null,
          id_Fabrica: materialForm.id_Fabrica || null,
        }
      : {
          NombreMaterial: materialForm.NombreMaterial,
          TipoMaterial: materialForm.TipoMaterial,
          Composicion: materialForm.Composicion,
          Peso: materialForm.Peso || null,
          AnchoTirada: materialForm.AnchoTirada || null,
          MinimumOrderQty: materialForm.MinimumOrderQty || null,
          Precio: materialForm.Precio || null,
          Proveedor: materialForm.Proveedor || null,
          CODPROV: materialForm.Proveedor || null,
          id_Fabrica: materialForm.id_Fabrica || null,
          MillName: materialForm.MillName || null,
          pedir: materialForm.pedir,
          Estado: materialForm.pedir ? "pendiente_llegar" : "catalogado",
        };
    const created = await addFeriaMaterial(edicionId, payload);
    if (materialForm.photoFile && created?.id_Materialoteca) {
      await uploadMaterialFotos(created.id_Materialoteca, [
        materialForm.photoFile,
      ]);
    }
    setMaterialOpen(false);
    await notifyChanged();
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {showHeader ? (
          <Box
            sx={{
              height: { xs: "auto", md: headerHeight },
              minHeight: { xs: 72, md: headerHeight },
              mb: 1.5,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              flexShrink: 0,
              boxSizing: "border-box",
              px: 1.5,
              py: { xs: 1.5, md: 1 },
            }}
          >
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t("FeriasPage.loadingEdition")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t("FeriasPage.loadingEdition")}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  if (!edicion) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 1, pl: 1 }}>
        {t("FeriasPage.loadEditionError")}
      </Typography>
    );
  }

  const sectionSx = {
    px: 2,
    py: 1.75,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    bgcolor: alpha(theme.palette.background.paper, 0.9),
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  };

  const peopleControls = (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{
        width: { xs: "100%", md: "auto" },
        minWidth: { md: 280 },
        flex: { md: 1 },
        maxWidth: { md: 480 },
      }}
    >
      <FormControlLabel
        sx={{ m: 0, flexShrink: 0 }}
        control={
          <Switch
            size="small"
            checked={!!edicion.Asistimos}
            onChange={async (e) => {
              await updateFeriaEdicion(edicionId, {
                Asistimos: e.target.checked,
              });
              await notifyChanged();
            }}
          />
        }
        label={
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t("FeriasPage.attending")}
          </Typography>
        }
      />
      <Autocomplete
        multiple
        size="small"
        sx={{ flex: 1, minWidth: { xs: "100%", sm: 160 } }}
        options={users}
        value={selectedUsers}
        getOptionLabel={(u) => u.username || ""}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={async (_, value) => {
          await updateFeriaAsistentes(
            edicionId,
            value.map((u) => u.id),
          );
          await notifyChanged();
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("FeriasPage.stagePeople")}
            size="small"
          />
        )}
      />
    </Stack>
  );

  const suppliersBlock = (
    <Box sx={sectionSx} id="feria-section-suppliers">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.25 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
            {t("FeriasPage.suppliersTitleShort")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("FeriasPage.selectSupplierToAddMaterials")}
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setSupplierMode("existing-trader");
            setSupplierForm(emptySupplierForm());
            setSupplierOpen(true);
          }}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {t("FeriasPage.addSupplier")}
        </Button>
      </Stack>
      <Stack spacing={0.75} sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {(edicion.proveedores || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("FeriasPage.noSuppliers")}
          </Typography>
        ) : (
          (edicion.proveedores || []).map((row) => {
            const selected =
              Number(selectedSupplierId) === Number(row.id_FeriaProveedor);
            const matCount = (edicion.materiales || []).filter((m) =>
              materialBelongsToSupplier(m, row),
            ).length;
            return (
              <Stack
                key={row.id_FeriaProveedor}
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => {
                  const next = selected ? null : row.id_FeriaProveedor;
                  setSelectedSupplierId(next);
                  setHighlightMaterialId(null);
                  if (onFocusItem) onFocusItem("suppliers", next);
                }}
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: 2,
                  cursor: "pointer",
                  border: `1px solid ${
                    selected
                      ? alpha(ACCENT, 0.85)
                      : alpha(theme.palette.divider, 0.6)
                  }`,
                  bgcolor: selected
                    ? alpha(ACCENT, 0.14)
                    : alpha(theme.palette.text.primary, 0.03),
                  "&:hover": {
                    bgcolor: selected
                      ? alpha(ACCENT, 0.2)
                      : alpha(theme.palette.text.primary, 0.06),
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.875rem" }}>
                    {row.proveedor?.N_PROVEEDOR ||
                      row.fabrica?.NombreFabrica ||
                      t("FeriasPage.unnamed")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[
                      row.proveedor
                        ? t("FeriasPage.kindTrader")
                        : t("FeriasPage.kindMill"),
                      row.Stand,
                      t("FeriasPage.supplierMaterialCount", {
                        count: matCount,
                      }),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                </Box>
                <Tooltip title={t("FeriasPage.addMaterialToSupplier")}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      openAddMaterialForSupplier(row);
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditSupplier(row);
                    setEditSupplierForm({
                      Nombre:
                        row.proveedor?.N_PROVEEDOR ||
                        row.fabrica?.NombreFabrica ||
                        "",
                      Pais: row.proveedor?.PAIS || row.fabrica?.Pais || "",
                      Email: row.proveedor?.EMAIL || row.fabrica?.Email || "",
                      Telefono:
                        row.proveedor?.TELEFONO || row.fabrica?.Telefono || "",
                      Direccion:
                        row.proveedor?.DIRECCION ||
                        row.fabrica?.Direccion ||
                        "",
                      NVIA: row.proveedor?.NVIA || "",
                      CP: row.proveedor?.CP || "",
                      Ciudad: row.proveedor?.POBLACION || "",
                      Provincia: row.proveedor?.PROVINCIA || "",
                      Stand: row.Stand || "",
                      Notas: row.Notas || "",
                      isTrader: Boolean(row.proveedor),
                    });
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await deleteFeriaProveedor(row.id_FeriaProveedor);
                    if (
                      Number(selectedSupplierId) ===
                      Number(row.id_FeriaProveedor)
                    ) {
                      setSelectedSupplierId(null);
                    }
                    await notifyChanged();
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })
        )}
      </Stack>
    </Box>
  );

  const selectedSupplierLabel =
    selectedSupplierRow?.proveedor?.N_PROVEEDOR ||
    selectedSupplierRow?.fabrica?.NombreFabrica ||
    "";

  const materialsBlock = (
    <Box sx={sectionSx} id="feria-section-materials">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.25 }}
        spacing={1}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
            {t("FeriasPage.materialsTitleShort")}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {selectedSupplierRow
              ? t("FeriasPage.materialsForSupplier", {
                  name: selectedSupplierLabel,
                })
              : t("FeriasPage.materialsSelectSupplierHint")}
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={!selectedSupplierRow}
          onClick={() => openAddMaterialForSupplier(selectedSupplierRow)}
          sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
        >
          {t("FeriasPage.addMaterial")}
        </Button>
      </Stack>
      <Stack spacing={0.75} sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {!selectedSupplierRow ? (
          <Typography variant="body2" color="text.secondary">
            {t("FeriasPage.materialsSelectSupplierHint")}
          </Typography>
        ) : filteredMaterials.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("FeriasPage.noMaterialsForSupplier")}
          </Typography>
        ) : (
          filteredMaterials.map((row) => {
            const thumb = materialFotoPublicUrl(
              materialPrimaryFotoArchivo(row.material),
            );
            const highlighted =
              Number(highlightMaterialId) === Number(row.id_FeriaMaterial);
            return (
              <Stack
                key={row.id_FeriaMaterial}
                id={`feria-material-${row.id_FeriaMaterial}`}
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => {
                  setHighlightMaterialId(row.id_FeriaMaterial);
                  if (onFocusItem) {
                    onFocusItem("materials", row.id_FeriaMaterial);
                  }
                }}
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: 2,
                  cursor: "pointer",
                  border: `1px solid ${
                    highlighted ? alpha(ACCENT, 0.9) : "transparent"
                  }`,
                  bgcolor: highlighted
                    ? alpha(ACCENT, 0.16)
                    : alpha(theme.palette.text.primary, 0.03),
                }}
              >
                <Box
                  onClick={(e) => {
                    if (!thumb) return;
                    e.stopPropagation();
                    setPhotoLightboxSrc(thumb);
                  }}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    overflow: "hidden",
                    bgcolor: alpha(theme.palette.text.primary, 0.06),
                    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: thumb ? "zoom-in" : "default",
                  }}
                >
                  {thumb ? (
                    <Box
                      component="img"
                      src={thumb}
                      alt=""
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <ImageOutlinedIcon
                      sx={{ fontSize: 22, color: "text.disabled" }}
                    />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.875rem" }}>
                    {row.material?.NombreMaterial || `#${row.id_Materialoteca}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[
                      row.material?.Composicion,
                      row.material?.Peso != null && row.material?.Peso !== ""
                        ? `${row.material.Peso} g/m²`
                        : null,
                      row.material?.AnchoTirada != null &&
                      row.material?.AnchoTirada !== ""
                        ? `${row.material.AnchoTirada} cm`
                        : null,
                      row.material?.MinimumOrderQty != null &&
                      row.material?.MinimumOrderQty !== ""
                        ? `MOQ ${row.material.MinimumOrderQty}`
                        : null,
                      row.material?.Precio != null &&
                      row.material?.Precio !== ""
                        ? `${row.material.Precio} €`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </Typography>
                </Box>
                <FeriaEstadoChip
                  estado={row.Estado}
                  editable
                  onChange={async (Estado) => {
                    await updateFeriaMaterial(row.id_FeriaMaterial, {
                      Estado,
                    });
                    await notifyChanged();
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditMaterial(row);
                    setEditMaterialForm({
                      NombreMaterial: row.material?.NombreMaterial || "",
                      Composicion: row.material?.Composicion || "",
                      MillName: row.material?.MillName || "",
                      Proveedor: row.CODPROV || row.material?.Proveedor || "",
                      id_Fabrica: row.id_Fabrica || "",
                      Peso: row.material?.Peso ?? "",
                      AnchoTirada: row.material?.AnchoTirada ?? "",
                      MinimumOrderQty: row.material?.MinimumOrderQty ?? "",
                      Precio: row.material?.Precio ?? "",
                      Estado: row.Estado || "catalogado",
                      Notas: row.Notas || "",
                      Fotos: row.material?.Fotos || [],
                    });
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={async () => {
                    await deleteFeriaMaterial(row.id_FeriaMaterial);
                    await notifyChanged();
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })
        )}
      </Stack>
    </Box>
  );

  return (
    <Box
      sx={{
        pb: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {showHeader ? (
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.5, md: 1 },
            mb: 1.5,
            height: { xs: "auto", md: headerHeight },
            minHeight: { xs: "auto", md: headerHeight },
            maxHeight: { xs: "none", md: headerHeight },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            background: `linear-gradient(125deg, ${alpha(ACCENT, 0.2)} 0%, ${theme.palette.background.paper} 60%)`,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: { xs: 1.25, md: 2 },
            flexShrink: 0,
            boxSizing: "border-box",
            overflow: { xs: "visible", md: "hidden" },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 800,
                color: "text.secondary",
                lineHeight: 1.2,
                display: "block",
              }}
            >
              {edicion.feria?.Nombre}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.05rem", md: "1.15rem" },
                lineHeight: 1.25,
              }}
            >
              {edicion.Anio}
              {edicion.Temporada ? ` · ${edicion.Temporada}` : ""}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25 }}
            >
              {[edicion.FechaInicio, edicion.FechaFin]
                .filter(Boolean)
                .join(" → ") || t("FeriasPage.noDates")}
              {edicion.feria?.Ciudad ? ` · ${edicion.feria.Ciudad}` : ""}
            </Typography>
          </Box>
          {peopleControls}
        </Box>
      ) : (
        <Box sx={{ mb: 1.5, flexShrink: 0 }}>{peopleControls}</Box>
      )}

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ flex: 1, minHeight: 0, alignItems: "stretch" }}
      >
        {suppliersBlock}
        {materialsBlock}
      </Stack>

      <Dialog
        open={supplierOpen}
        onClose={() => setSupplierOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t("FeriasPage.addSupplier")}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2.25}>
            <TextField
              select
              size="small"
              label={t("FeriasPage.supplierMode")}
              value={supplierMode}
              onChange={(e) => setSupplierMode(e.target.value)}
              fullWidth
              sx={inputSx}
            >
              <MenuItem value="existing-trader">
                {t("FeriasPage.modeExistingTrader")}
              </MenuItem>
              <MenuItem value="existing-mill">
                {t("FeriasPage.modeExistingMill")}
              </MenuItem>
              <MenuItem value="new">{t("FeriasPage.modeNew")}</MenuItem>
            </TextField>
            {supplierMode === "existing-trader" && (
              <Autocomplete
                options={traders}
                getOptionLabel={(p) =>
                  `${p.N_PROVEEDOR || ""}${p.PAIS ? ` (${p.PAIS})` : ""}`
                }
                onChange={(_, v) =>
                  setSupplierForm((prev) => ({
                    ...prev,
                    CODPROV: v?.CODPROV || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("FeriasPage.kindTrader")}
                    sx={inputSx}
                  />
                )}
              />
            )}
            {supplierMode === "existing-mill" && (
              <Autocomplete
                options={mills}
                getOptionLabel={(p) => p.NombreFabrica || ""}
                onChange={(_, v) =>
                  setSupplierForm((prev) => ({
                    ...prev,
                    id_Fabrica: v?.id_Fabrica || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("FeriasPage.kindMill")}
                    sx={inputSx}
                  />
                )}
              />
            )}
            {supplierMode === "new" && (
              <>
                {photoButtons("card")}
                {supplierForm.photoFile ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("FeriasPage.photoReady", {
                      name: supplierForm.photoFile.name,
                    })}
                  </Typography>
                ) : null}
                <FormSection
                  icon={BusinessOutlinedIcon}
                  title={t("FeriasPage.sectionSupplier")}
                >
                  <TextField
                    select
                    size="small"
                    label={t("FeriasPage.kind")}
                    value={supplierForm.kind}
                    onChange={(e) =>
                      patchSupplierForm({ kind: e.target.value })
                    }
                    fullWidth
                    sx={inputSx}
                  >
                    <MenuItem value="trader">
                      {t("FeriasPage.kindTrader")}
                    </MenuItem>
                    <MenuItem value="mill">{t("FeriasPage.kindMill")}</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    label={t("FeriasPage.labelName")}
                    value={supplierForm.Nombre}
                    onChange={(e) =>
                      patchSupplierForm({ Nombre: e.target.value })
                    }
                    required
                    fullWidth
                    sx={inputSx}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      size="small"
                      label={t("FeriasPage.labelEmail")}
                      value={supplierForm.Email}
                      onChange={(e) =>
                        patchSupplierForm({ Email: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      size="small"
                      label={t("FeriasPage.labelPhone")}
                      value={supplierForm.Telefono}
                      onChange={(e) =>
                        patchSupplierForm({ Telefono: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                  </Stack>
                </FormSection>
                <Divider />
                <FormSection
                  icon={PlaceOutlinedIcon}
                  title={t("FeriasPage.sectionAddress")}
                >
                  <TextField
                    size="small"
                    label={t("ProveedoresPage.labelStreetAddress")}
                    value={supplierForm.Direccion}
                    onChange={(e) =>
                      patchSupplierForm({ Direccion: e.target.value })
                    }
                    fullWidth
                    sx={inputSx}
                  />
                  {supplierForm.kind === "trader" ? (
                    <>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                      >
                        <TextField
                          size="small"
                          label={t("ProveedoresPage.labelStreetNumber")}
                          value={supplierForm.NVIA}
                          onChange={(e) =>
                            patchSupplierForm({ NVIA: e.target.value })
                          }
                          sx={{ ...inputSx, width: { xs: "100%", sm: 100 } }}
                        />
                        <TextField
                          size="small"
                          label={t("ProveedoresPage.labelPostalShort")}
                          value={supplierForm.CP}
                          onChange={(e) =>
                            patchSupplierForm({ CP: e.target.value })
                          }
                          sx={{ ...inputSx, width: { xs: "100%", sm: 120 } }}
                        />
                        <TextField
                          size="small"
                          label={t("ProveedoresPage.labelCityEn")}
                          value={supplierForm.Ciudad}
                          onChange={(e) =>
                            patchSupplierForm({ Ciudad: e.target.value })
                          }
                          fullWidth
                          sx={inputSx}
                        />
                      </Stack>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                      >
                        <TextField
                          size="small"
                          label={t("ProveedoresPage.labelProvinceEn")}
                          value={supplierForm.Provincia}
                          onChange={(e) =>
                            patchSupplierForm({ Provincia: e.target.value })
                          }
                          fullWidth
                          sx={inputSx}
                        />
                        <TextField
                          size="small"
                          label={t("FeriasPage.labelCountry")}
                          value={supplierForm.Pais}
                          onChange={(e) =>
                            patchSupplierForm({ Pais: e.target.value })
                          }
                          fullWidth
                          sx={inputSx}
                        />
                      </Stack>
                    </>
                  ) : (
                    <TextField
                      size="small"
                      label={t("FeriasPage.labelCountry")}
                      value={supplierForm.Pais}
                      onChange={(e) =>
                        patchSupplierForm({ Pais: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                  )}
                </FormSection>
              </>
            )}
            <Divider />
            <FormSection
              icon={StorefrontOutlinedIcon}
              title={t("FeriasPage.sectionFairData")}
            >
              <TextField
                size="small"
                label={t("FeriasPage.labelStand")}
                value={supplierForm.Stand}
                onChange={(e) => patchSupplierForm({ Stand: e.target.value })}
                fullWidth
                sx={inputSx}
              />
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSupplierOpen(false)}>
            {t("FeriasPage.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSaveSupplier}>
            {t("FeriasPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={materialOpen}
        onClose={() => setMaterialOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t("FeriasPage.addMaterial")}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2.25}>
            <Autocomplete
              options={materiales}
              getOptionLabel={(m) => m.NombreMaterial || ""}
              onChange={(_, v) =>
                setMaterialForm((prev) => ({
                  ...prev,
                  id_Materialoteca: v?.Id || "",
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={t("FeriasPage.existingMaterial")}
                  helperText={t("FeriasPage.existingMaterialHint")}
                  sx={inputSx}
                />
              )}
            />
            <TextField
              size="small"
              label={t("FeriasPage.materialSupplier")}
              value={
                selectedMaterialSupplier
                  ? `${selectedMaterialSupplier.label}${
                      selectedMaterialSupplier.kind === "mill"
                        ? ` (${t("FeriasPage.kindMill")})`
                        : ` (${t("FeriasPage.kindTrader")})`
                    }`
                  : selectedSupplierLabel || ""
              }
              InputProps={{ readOnly: true }}
              helperText={t("FeriasPage.materialSupplierLockedHint")}
              fullWidth
              sx={inputSx}
            />
            {!materialForm.id_Materialoteca && (
              <>
                <FormSection
                  icon={ImageOutlinedIcon}
                  title={t("FeriasPage.fromPhoto")}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("FeriasPage.extractMaterialHint")}
                  </Typography>
                  {photoButtons("material")}
                  {materialForm.extractName ? (
                    <Typography variant="caption" color="text.secondary">
                      {t("FeriasPage.dataReadFrom", {
                        name: materialForm.extractName,
                      })}
                    </Typography>
                  ) : null}
                </FormSection>
                <Divider />
                <FormSection
                  icon={PhotoCameraOutlinedIcon}
                  title={t("MaterialotecaPage.sectionPhoto")}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("FeriasPage.materialPhotoHint")}
                  </Typography>
                  {photoButtons("material-photo", {
                    takeLabel: t("FeriasPage.takeMaterialPhoto"),
                    galleryLabel: t("FeriasPage.chooseMaterialPhoto"),
                  })}
                  {materialForm.photoFile ? (
                    <Typography variant="caption" color="text.secondary">
                      {t("FeriasPage.photoReady", {
                        name: materialForm.photoFile.name,
                      })}
                    </Typography>
                  ) : null}
                </FormSection>
                <Divider />
                <FormSection
                  icon={CategoryOutlinedIcon}
                  title={t("FeriasPage.sectionMaterial")}
                >
                  <TextField
                    size="small"
                    label={t("MaterialotecaPage.labelMaterialName")}
                    value={materialForm.NombreMaterial}
                    onChange={(e) =>
                      patchMaterialForm({ NombreMaterial: e.target.value })
                    }
                    required
                    fullWidth
                    sx={inputSx}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      select
                      size="small"
                      label={t("MaterialotecaPage.labelGroup")}
                      value={materialForm.ID_GRUPO}
                      onChange={(e) =>
                        patchMaterialForm({
                          ID_GRUPO: e.target.value,
                          TipoMaterial: "",
                        })
                      }
                      fullWidth
                      sx={inputSx}
                    >
                      {grupos.map((g) => (
                        <MenuItem key={g.ID_GRUPO} value={g.ID_GRUPO}>
                          {g.GRUPO}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label={t("MaterialotecaPage.labelSubgroup")}
                      value={materialForm.TipoMaterial}
                      onChange={(e) =>
                        patchMaterialForm({ TipoMaterial: e.target.value })
                      }
                      disabled={!materialForm.ID_GRUPO}
                      fullWidth
                      sx={inputSx}
                    >
                      {subgrupos.map((s) => (
                        <MenuItem key={s.ID_SUBGRUPO} value={s.ID_SUBGRUPO}>
                          {s.SUBGRUPO}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  <TextField
                    size="small"
                    label={t("MaterialotecaPage.labelComposition")}
                    value={materialForm.Composicion}
                    onChange={(e) =>
                      patchMaterialForm({ Composicion: e.target.value })
                    }
                    fullWidth
                    sx={inputSx}
                  />
                </FormSection>
                <Divider />
                <FormSection
                  icon={StraightenOutlinedIcon}
                  title={t("MaterialotecaPage.sectionSpecs")}
                >
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      size="small"
                      type="number"
                      label={t("MaterialotecaPage.labelWeight")}
                      value={materialForm.Peso}
                      onChange={(e) =>
                        patchMaterialForm({ Peso: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label={t("MaterialotecaPage.labelWidth")}
                      value={materialForm.AnchoTirada}
                      onChange={(e) =>
                        patchMaterialForm({ AnchoTirada: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label={t("MaterialotecaPage.labelMoq")}
                      value={materialForm.MinimumOrderQty}
                      onChange={(e) =>
                        patchMaterialForm({ MinimumOrderQty: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label={t("MaterialotecaPage.labelPrice")}
                      value={materialForm.Precio}
                      onChange={(e) =>
                        patchMaterialForm({ Precio: e.target.value })
                      }
                      inputProps={{ step: "0.01" }}
                      fullWidth
                      sx={inputSx}
                    />
                  </Stack>
                  <TextField
                    size="small"
                    label={t("FeriasPage.labelMillName")}
                    value={materialForm.MillName}
                    onChange={(e) =>
                      patchMaterialForm({ MillName: e.target.value })
                    }
                    fullWidth
                    sx={inputSx}
                  />
                </FormSection>
              </>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={materialForm.pedir}
                  onChange={(e) =>
                    patchMaterialForm({ pedir: e.target.checked })
                  }
                />
              }
              label={t("FeriasPage.orderSample")}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMaterialOpen(false)}>
            {t("FeriasPage.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSaveMaterial}>
            {t("FeriasPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <ExpenseUserWebcamDialog
        open={webcamOpen}
        onClose={() => setWebcamOpen(false)}
        t={t}
        onCapture={async (file) => {
          if (webcamKind === "material-photo") {
            patchMaterialForm({ photoFile: file });
            return;
          }
          await handlePhotoExtract(file, webcamKind);
        }}
      />

      <Dialog
        open={Boolean(editSupplier)}
        onClose={() => setEditSupplier(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t("FeriasPage.editSupplier")}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2.25}>
            <FormSection
              icon={BusinessOutlinedIcon}
              title={t("FeriasPage.sectionSupplier")}
            >
              <TextField
                size="small"
                label={t("FeriasPage.labelName")}
                value={editSupplierForm.Nombre}
                onChange={(e) => patchEditSupplier({ Nombre: e.target.value })}
                required
                fullWidth
                sx={inputSx}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  size="small"
                  label={t("FeriasPage.labelEmail")}
                  value={editSupplierForm.Email}
                  onChange={(e) => patchEditSupplier({ Email: e.target.value })}
                  fullWidth
                  sx={inputSx}
                />
                <TextField
                  size="small"
                  label={t("FeriasPage.labelPhone")}
                  value={editSupplierForm.Telefono}
                  onChange={(e) =>
                    patchEditSupplier({ Telefono: e.target.value })
                  }
                  fullWidth
                  sx={inputSx}
                />
              </Stack>
            </FormSection>

            <Divider />

            <FormSection
              icon={PlaceOutlinedIcon}
              title={t("FeriasPage.sectionAddress")}
            >
              <TextField
                size="small"
                label={t("ProveedoresPage.labelStreetAddress")}
                value={editSupplierForm.Direccion}
                onChange={(e) =>
                  patchEditSupplier({ Direccion: e.target.value })
                }
                fullWidth
                sx={inputSx}
              />
              {editSupplierForm.isTrader ? (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      size="small"
                      label={t("ProveedoresPage.labelStreetNumber")}
                      value={editSupplierForm.NVIA}
                      onChange={(e) =>
                        patchEditSupplier({ NVIA: e.target.value })
                      }
                      sx={{ ...inputSx, width: { xs: "100%", sm: 100 } }}
                    />
                    <TextField
                      size="small"
                      label={t("ProveedoresPage.labelPostalShort")}
                      value={editSupplierForm.CP}
                      onChange={(e) =>
                        patchEditSupplier({ CP: e.target.value })
                      }
                      sx={{ ...inputSx, width: { xs: "100%", sm: 120 } }}
                    />
                    <TextField
                      size="small"
                      label={t("ProveedoresPage.labelCityEn")}
                      value={editSupplierForm.Ciudad}
                      onChange={(e) =>
                        patchEditSupplier({ Ciudad: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      size="small"
                      label={t("ProveedoresPage.labelProvinceEn")}
                      value={editSupplierForm.Provincia}
                      onChange={(e) =>
                        patchEditSupplier({ Provincia: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      size="small"
                      label={t("FeriasPage.labelCountry")}
                      value={editSupplierForm.Pais}
                      onChange={(e) =>
                        patchEditSupplier({ Pais: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                  </Stack>
                </>
              ) : (
                <TextField
                  size="small"
                  label={t("FeriasPage.labelCountry")}
                  value={editSupplierForm.Pais}
                  onChange={(e) => patchEditSupplier({ Pais: e.target.value })}
                  fullWidth
                  sx={inputSx}
                />
              )}
            </FormSection>

            <Divider />

            <FormSection
              icon={StorefrontOutlinedIcon}
              title={t("FeriasPage.sectionFairData")}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  size="small"
                  label={t("FeriasPage.labelStand")}
                  value={editSupplierForm.Stand}
                  onChange={(e) => patchEditSupplier({ Stand: e.target.value })}
                  fullWidth
                  sx={inputSx}
                />
              </Stack>
              <TextField
                size="small"
                label={t("FeriasPage.labelNotes")}
                value={editSupplierForm.Notas}
                onChange={(e) => patchEditSupplier({ Notas: e.target.value })}
                fullWidth
                multiline
                minRows={2}
                sx={inputSx}
              />
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditSupplier(null)}>
            {t("FeriasPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!editSupplier) return;
              await updateFeriaProveedor(editSupplier.id_FeriaProveedor, {
                Nombre: editSupplierForm.Nombre,
                Pais: editSupplierForm.Pais,
                Email: editSupplierForm.Email,
                Telefono: editSupplierForm.Telefono,
                Direccion: editSupplierForm.Direccion,
                NVIA: editSupplierForm.NVIA,
                CP: editSupplierForm.CP,
                Ciudad: editSupplierForm.Ciudad,
                Provincia: editSupplierForm.Provincia,
                Stand: editSupplierForm.Stand,
                Notas: editSupplierForm.Notas,
              });
              setEditSupplier(null);
              await notifyChanged();
            }}
          >
            {t("FeriasPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editMaterial)}
        onClose={() => setEditMaterial(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t("FeriasPage.editMaterial")}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2.25}>
            <FormSection
              icon={CategoryOutlinedIcon}
              title={t("FeriasPage.sectionMaterial")}
            >
              <Autocomplete
                options={supplierOptions}
                value={selectedEditMaterialSupplier}
                getOptionLabel={(o) =>
                  o
                    ? `${o.label}${
                        o.kind === "mill"
                          ? ` (${t("FeriasPage.kindMill")})`
                          : ` (${t("FeriasPage.kindTrader")})`
                      }`
                    : ""
                }
                isOptionEqualToValue={(a, b) => a?.key === b?.key}
                onChange={(_, v) =>
                  applySupplierToMaterialForm(v, patchEditMaterial)
                }
                noOptionsText={t("FeriasPage.noEditionSuppliersForMaterial")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    required
                    label={t("FeriasPage.materialSupplier")}
                    helperText={t("FeriasPage.materialSupplierHint")}
                    sx={inputSx}
                  />
                )}
              />
              <TextField
                size="small"
                label={t("MaterialotecaPage.labelMaterialName")}
                value={editMaterialForm.NombreMaterial}
                onChange={(e) =>
                  patchEditMaterial({ NombreMaterial: e.target.value })
                }
                required
                fullWidth
                sx={inputSx}
              />
              <TextField
                size="small"
                label={t("MaterialotecaPage.labelComposition")}
                value={editMaterialForm.Composicion}
                onChange={(e) =>
                  patchEditMaterial({ Composicion: e.target.value })
                }
                fullWidth
                sx={inputSx}
              />
              <TextField
                size="small"
                label={t("FeriasPage.labelMillName")}
                value={editMaterialForm.MillName}
                onChange={(e) =>
                  patchEditMaterial({ MillName: e.target.value })
                }
                fullWidth
                sx={inputSx}
              />
            </FormSection>

            <Divider />

            <FormSection
              icon={StraightenOutlinedIcon}
              title={t("MaterialotecaPage.sectionSpecs")}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  size="small"
                  type="number"
                  label={t("MaterialotecaPage.labelWeight")}
                  value={editMaterialForm.Peso}
                  onChange={(e) => patchEditMaterial({ Peso: e.target.value })}
                  fullWidth
                  sx={inputSx}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t("MaterialotecaPage.labelWidth")}
                  value={editMaterialForm.AnchoTirada}
                  onChange={(e) =>
                    patchEditMaterial({ AnchoTirada: e.target.value })
                  }
                  fullWidth
                  sx={inputSx}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t("MaterialotecaPage.labelMoq")}
                  value={editMaterialForm.MinimumOrderQty}
                  onChange={(e) =>
                    patchEditMaterial({ MinimumOrderQty: e.target.value })
                  }
                  fullWidth
                  sx={inputSx}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t("MaterialotecaPage.labelPrice")}
                  value={editMaterialForm.Precio}
                  onChange={(e) =>
                    patchEditMaterial({ Precio: e.target.value })
                  }
                  inputProps={{ step: "0.01" }}
                  fullWidth
                  sx={inputSx}
                />
              </Stack>
            </FormSection>

            <Divider />

            <FormSection
              icon={PhotoCameraOutlinedIcon}
              title={t("MaterialotecaPage.sectionPhoto")}
            >
              {(editMaterialForm.Fotos || []).length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {(editMaterialForm.Fotos || []).map((foto, idx) => {
                    const src = materialFotoPublicUrl(foto.Archivo);
                    if (!src) return null;
                    return (
                      <Box
                        key={`${foto.id_MaterialotecaFoto || "legacy"}-${foto.Archivo}-${idx}`}
                        sx={{
                          position: "relative",
                          width: 88,
                          height: 88,
                          borderRadius: 2,
                          overflow: "hidden",
                          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                        }}
                      >
                        <Box
                          component="img"
                          src={src}
                          alt=""
                          onClick={() => setPhotoLightboxSrc(src)}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            cursor: "zoom-in",
                          }}
                        />
                        <IconButton
                          size="small"
                          disabled={editPhotoBusy}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveEditMaterialPhoto(foto);
                          }}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            bgcolor: "rgba(0,0,0,0.5)",
                            color: "#fff",
                            p: "2px",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                          }}
                          aria-label={t("FeriasPage.removePhoto")}
                        >
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("FeriasPage.noMaterialPhotos")}
                </Typography>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  startIcon={<PhotoCameraOutlinedIcon />}
                  variant="outlined"
                  disabled={editPhotoBusy}
                  onClick={pickEditMaterialPhotos}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  {editPhotoBusy
                    ? t("FeriasPage.uploadingPhotos")
                    : t("FeriasPage.addMaterialPhotos")}
                </Button>
              </Stack>
            </FormSection>

            <Divider />

            <FormSection
              icon={NotesOutlinedIcon}
              title={t("FeriasPage.sectionFairData")}
            >
              <TextField
                select
                size="small"
                label={t("FeriasPage.estadoLabel")}
                value={editMaterialForm.Estado}
                onChange={(e) => patchEditMaterial({ Estado: e.target.value })}
                fullWidth
                sx={inputSx}
              >
                {MUESTRA_ESTADOS.map((st) => (
                  <MenuItem key={st} value={st}>
                    {t(`FeriasPage.estado.${st}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label={t("FeriasPage.labelNotes")}
                value={editMaterialForm.Notas}
                onChange={(e) => patchEditMaterial({ Notas: e.target.value })}
                fullWidth
                multiline
                minRows={2}
                sx={inputSx}
              />
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditMaterial(null)}>
            {t("FeriasPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!editMaterial) return;
              await updateFeriaMaterial(editMaterial.id_FeriaMaterial, {
                NombreMaterial: editMaterialForm.NombreMaterial,
                Composicion: editMaterialForm.Composicion,
                MillName: editMaterialForm.MillName,
                Proveedor: editMaterialForm.Proveedor || null,
                CODPROV: editMaterialForm.Proveedor || null,
                id_Fabrica: editMaterialForm.id_Fabrica || null,
                Peso:
                  editMaterialForm.Peso === "" ? null : editMaterialForm.Peso,
                AnchoTirada:
                  editMaterialForm.AnchoTirada === ""
                    ? null
                    : editMaterialForm.AnchoTirada,
                MinimumOrderQty:
                  editMaterialForm.MinimumOrderQty === ""
                    ? null
                    : editMaterialForm.MinimumOrderQty,
                Precio:
                  editMaterialForm.Precio === ""
                    ? null
                    : editMaterialForm.Precio,
                Estado: editMaterialForm.Estado,
                Notas: editMaterialForm.Notas,
              });
              setEditMaterial(null);
              await notifyChanged();
            }}
          >
            {t("FeriasPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <MaterialotecaPhotoLightbox
        open={Boolean(photoLightboxSrc)}
        src={photoLightboxSrc}
        alt={
          editMaterialForm.NombreMaterial ||
          editMaterial?.material?.NombreMaterial ||
          ""
        }
        onClose={() => setPhotoLightboxSrc(null)}
        closeAriaLabel={t("MaterialotecaPage.photoLightboxCloseAria")}
      />
    </Box>
  );
};

export default FeriaEdicionPanel;
